/**
 * GNews Service
 *
 * Fetches artist/topic headlines from GNews API via backend proxy.
 * Used for Cultural Intelligence panel headline display.
 */

import { apiClient } from '@shared/services/apiClient';

export interface HeadlineArticle {
  title: string;
  description: string | null;
  url: string;
  image: string | null;
  publishedAt: string;
  source: string;
}

export interface HeadlinesResponse {
  query: string;
  totalArticles: number;
  articles: HeadlineArticle[];
}

// Simple in-memory cache
const cache: Map<string, { data: HeadlinesResponse; expiresAt: number }> = new Map();
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Get latest headlines for a search query (artist name, topic, etc.)
 *
 * @param query - Search query (e.g., artist name)
 * @param maxResults - Number of articles (1-10, default 3)
 * @returns Headlines response or null if unavailable
 */
export async function getHeadlines(
  query: string,
  maxResults: number = 3
): Promise<HeadlinesResponse | null> {
  const cacheKey = `${query.toLowerCase()}:${maxResults}`;

  // Check cache
  const cached = cache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.data;
  }

  try {
    const response = await apiClient.get<HeadlinesResponse>('/gnews/headlines', {
      params: { query, max_results: maxResults },
    });

    // Cache the result
    cache.set(cacheKey, {
      data: response.data,
      expiresAt: Date.now() + CACHE_TTL_MS,
    });

    return response.data;
  } catch (error) {
    console.error('[GNews] Failed to fetch headlines:', error);
    return null;
  }
}

/**
 * Format a date string to relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}
